import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import ResponsiveHeader from '../components/ResponsiveHeader';
import Footer from '../components/Footer';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const StripeSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [packageInfo, setPackageInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const type = searchParams.get('type');
    
    if (sessionId) {
      pollPaymentStatus(sessionId, type);
    } else {
      setLoading(false);
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, type, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setLoading(false);
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/stripe/checkout-status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.status === 'paid') {
        setLoading(false);
        setStatus('success');
        setPackageInfo({
          type: data.package_type,
          name: data.package_name
        });
        toast({
          title: "Payment Successful! 🎉",
          description: data.message || "Your purchase has been processed.",
        });
        return;
      } else if (data.status === 'expired') {
        setLoading(false);
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, type, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts >= maxAttempts - 1) {
        setLoading(false);
        setStatus('error');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, type, attempts + 1), pollInterval);
      }
    }
  };

  const getRedirectPath = () => {
    if (packageInfo?.type === 'shop') {
      return '/orders';
    }
    return '/trainer/dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <ResponsiveHeader />
        <div className="container max-w-lg mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="p-8">
              <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Processing Payment...
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we confirm your payment.
              </p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <ResponsiveHeader />
        <div className="container max-w-lg mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="p-8">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Payment Successful!
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Your payment has been processed successfully.
              </p>
              {packageInfo?.name && (
                <p className="text-purple-600 font-semibold mb-6">
                  {packageInfo.name}
                </p>
              )}
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate(getRedirectPath())}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {packageInfo?.type === 'shop' ? 'View Orders' : 'Go to Dashboard'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Error or timeout state
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <ResponsiveHeader />
      <div className="container max-w-lg mx-auto px-4 py-16">
        <Card className="text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {status === 'expired' ? 'Session Expired' : 'Payment Verification Failed'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t verify your payment. Please check your email for confirmation or contact support.'}
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate(-1)}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                Try Again
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default StripeSuccessPage;
